import axios from 'axios'

const DEFAULT_API_URL = `https://api-ls.talkpro.com/api`
const API_URL =
  process.env.NODE_ENV === 'development'
    ? DEFAULT_API_URL
    : process.env.REACT_APP_SERVER_URL || DEFAULT_API_URL

if (process.env.NODE_ENV === 'development') {
  // Helps confirm which API host local builds are actually using.
  // eslint-disable-next-line no-console
  console.info('[talkpro] API base URL:', API_URL)
}

export const api = axios.create({
  baseURL: API_URL,
})

let isRefreshingToken = false
let refreshSubscribers = []

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback)
}

const onTokenRefreshed = (nextToken) => {
  refreshSubscribers.forEach((callback) => callback(nextToken))
  refreshSubscribers = []
}

api.interceptors.request.use(
  async function refreshInterceptor(config) {
    const token = window.localStorage.getItem('token')
    const csrfToken = document.cookie
      .split('; ')
      .find((row) => row.startsWith('csrftoken='))
      ?.split('=')[1]

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    if (csrfToken) {
      config.xsrfCookieName = 'csrftoken'
      config.xsrfHeaderName = 'X-CSRFToken'
      config.headers['X-CSRFToken'] = csrfToken
    }

    return config
  },
  (e) => Promise.reject(e)
)

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config
    const status = error?.response?.status
    const refreshToken = window.localStorage.getItem('refreshToken')

    if (!originalRequest || status !== 401 || originalRequest._retry) {
      return Promise.reject(error)
    }

    if (
      originalRequest?.url?.includes('/auth/token/refresh/') ||
      originalRequest?.url?.includes('/auth/logout/')
    ) {
      return Promise.reject(error)
    }

    if (!refreshToken) {
      return Promise.reject(error)
    }

    originalRequest._retry = true

    if (isRefreshingToken) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((nextToken) => {
          if (!nextToken) {
            reject(error)
            return
          }
          originalRequest.headers.Authorization = `Bearer ${nextToken}`
          resolve(api(originalRequest))
        })
      })
    }

    isRefreshingToken = true

    try {
      const refreshRes = await axios.post(`${API_URL}/auth/token/refresh/`, {
        refresh: refreshToken,
      })
      const nextToken = refreshRes?.data?.access || refreshRes?.data?.access_token
      const nextRefreshToken =
        refreshRes?.data?.refresh || refreshRes?.data?.refresh_token

      if (!nextToken) {
        throw new Error('Failed to refresh access token')
      }

      window.localStorage.setItem('token', nextToken)
      if (nextRefreshToken) {
        window.localStorage.setItem('refreshToken', nextRefreshToken)
      }

      onTokenRefreshed(nextToken)
      originalRequest.headers.Authorization = `Bearer ${nextToken}`
      return api(originalRequest)
    } catch (refreshError) {
      onTokenRefreshed(null)
      return Promise.reject(refreshError)
    } finally {
      isRefreshingToken = false
    }
  }
)
